import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, SearchIcon, ListIcon, MapIcon } from 'lucide-react';
import { stockistRegions, stockistBenefits } from '../data/siteContent';
import { PageHero } from '../components/PageHero';
import { SectionHeading } from '../components/SectionHeading';
import { EnquiryForm } from '../components/EnquiryForm';
import { StockistMap } from '../components/StockistMap';
const EASE = [0.23, 1, 0.32, 1] as const;
const REGION_VIEW: Record<string, {
  center: [number, number];
  zoom: number;
}> = {
  all: {
    center: [-29.5, 25.0],
    zoom: 5
  },
  'western-cape': {
    center: [-33.93, 18.6],
    zoom: 9
  },
  gauteng: {
    center: [-26.15, 28.03],
    zoom: 10
  },
  kzn: {
    center: [-29.62, 30.48],
    zoom: 8
  },
  'eastern-cape': {
    center: [-33.4, 26.7],
    zoom: 7
  }
};
export function Stockists() {
  const [query, setQuery] = useState('');
  const [region, setRegion] = useState<string | null>(null);
  const [view, setView] = useState<'list' | 'map'>('list');
  const visible = stockistRegions.filter(r => !region || r.id === region).map(r => ({
    ...r,
    stores: r.stores.filter(s => !query || s.name.toLowerCase().includes(query.toLowerCase()) || s.city.toLowerCase().includes(query.toLowerCase()))
  })).filter(r => r.stores.length > 0);
  const pins = visible.flatMap(r => r.stores.map(s => ({
    ...s,
    region: r.region
  })));
  const mapView = REGION_VIEW[region ?? 'all'];
  return <div className="w-full bg-paper" data-id="element-928" data-mp="2zh8v4d4k2c8n">
      <PageHero eyebrow="Stockists" title="Find us, or stock us." intro="Over forty independent cafés, roasteries and design stores carry Ecoffee Cup® across South Africa." data-id="element-929" data-mp="2ze358vo3r6bv">
        <div className="flex flex-wrap gap-3" data-id="element-930" data-mp="2pk43hxrxxokg">
          <a href="#find" className="type-cta rounded-full bg-coffee px-8 py-4 text-[11px] text-paper transition-colors hover:bg-pennant" data-id="element-931" data-mp="1j2qqdh4zf01v">
            Find a stockist
          </a>
          <a href="#become" className="type-cta rounded-full border border-coffee/25 px-8 py-4 text-[11px] text-coffee transition-colors hover:bg-coffee hover:text-paper" data-id="element-932" data-mp="63nkth8p9vxh">
            Become a stockist
          </a>
        </div>
      </PageHero>

      {/* Find a stockist */}
      <section id="find" className="mx-auto max-w-[1400px] scroll-mt-28 px-6 py-20 md:py-28" data-id="element-933" data-mp="127zrp6ivpf3">
        <SectionHeading eyebrow="Find a stockist" title="Where to buy in person." action={<div className="flex rounded-full border border-oat p-1" role="group" aria-label="View stockists as" data-id="element-935" data-mp="39k4bd2gv5k7q">
              <button onClick={() => setView('list')} aria-pressed={view === 'list'} className={`type-cta inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[10px] transition-colors ${view === 'list' ? 'bg-coffee text-paper' : 'text-coffee/60 hover:text-coffee'}`} data-id="element-936" data-mp="3egl284fg6coi">
                <ListIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-937" data-mp="2ux5q5p8lnm1g" />
                List
              </button>
              <button onClick={() => setView('map')} aria-pressed={view === 'map'} className={`type-cta inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[10px] transition-colors ${view === 'map' ? 'bg-coffee text-paper' : 'text-coffee/60 hover:text-coffee'}`} data-id="element-938" data-mp="2pxjcxfsru5sn">
                <MapIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-939" data-mp="38oais7djve2a" />
                Map
              </button>
            </div>} data-id="element-934" data-mp="3iiprys2ryp5v" />

        <div className="mt-12 flex flex-col gap-4 md:flex-row md:items-center" data-id="element-940" data-mp="3njqhwqszm05j">
          <div className="relative flex-1" data-id="element-941" data-mp="3iob5m3amocb8">
            <SearchIcon className="pointer-events-none absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/35" strokeWidth={1.5} data-id="element-942" data-mp="1g0cj5r698ysf" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by store or city" aria-label="Search stockists" className="type-body w-full rounded-full border border-oat bg-paper py-4 pl-12 pr-5 text-sm outline-none transition-colors placeholder:text-ink/35 focus:border-coffee" data-id="element-943" data-mp="1kwt7aldgfd5i" />
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-944" data-mp="16zect28cgf6o">
            <button onClick={() => setRegion(null)} aria-pressed={region === null} className={`type-cta rounded-full border px-4 py-2.5 text-[10px] transition-colors ${region === null ? 'border-coffee bg-coffee text-paper' : 'border-oat text-coffee/70 hover:border-coffee'}`} data-id="element-945" data-mp="16ylfssz6t41q">
              All
            </button>
            {stockistRegions.map(r => <button key={r.id} onClick={() => setRegion(region === r.id ? null : r.id)} aria-pressed={region === r.id} className={`type-cta rounded-full border px-4 py-2.5 text-[10px] transition-colors ${region === r.id ? 'border-coffee bg-coffee text-paper' : 'border-oat text-coffee/70 hover:border-coffee'}`} data-id="element-946" data-mp="6qy6ufjy20z0">
                {r.region}
              </button>)}
          </div>
        </div>

        {view === 'map' ? <div className="mt-12" data-id="element-947" data-mp="3a8q4oakrhf54">
            <StockistMap stockists={pins} center={mapView.center} zoom={mapView.zoom} data-id="element-948" data-mp="3a9hqitok3yi7" />
            <p className="type-body mt-4 text-xs text-ink/45" data-id="element-949" data-mp="18gtim70ywmw3">
              {pins.length} stockists shown. Tap a marker for the address.
            </p>
          </div> : <div className="mt-14 space-y-12" data-id="element-950" data-mp="32uxky1uot4oj">
            {visible.map(r => <div key={r.id} data-id="element-951" data-mp="37oti5234mnin">
                <p className="type-eyebrow mb-6 text-[10px] text-pennant" data-id="element-952" data-mp="2syulx4v4tm5t">
                  {r.region}
                </p>
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-953" data-mp="2xww65t4anriv">
                  {r.stores.map((s, i) => <motion.div key={s.name} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-40px'
            }} transition={{
              duration: 0.4,
              delay: i * 0.04,
              ease: EASE
            }} className="rounded-3xl border border-oat/70 bg-paper-warm p-7" data-id="element-954" data-mp="3lokz9boxwk0v" data-mpi={i}>
                      <MapPinIcon className="mb-4 h-4 w-4 text-coffee/45" strokeWidth={1.5} data-id="element-955" data-mp="256xahevlgw58" />
                      <p className="type-ui text-base text-coffee" data-id="element-956" data-mp="ibim0657w3xf">{s.name}</p>
                      <p className="type-body mt-1 text-sm text-ink/55" data-id="element-957" data-mp="wccx4wjngizs">
                        {s.address}
                      </p>
                      <p className="type-eyebrow mt-3 text-[9px] text-coffee/40" data-id="element-958" data-mp="sazu0u7xytwk">
                        {s.city}
                      </p>
                    </motion.div>)}
                </div>
              </div>)}
            {visible.length === 0 && <p className="type-body py-16 text-center text-sm text-ink/55" data-id="element-959" data-mp="de0n78kwz535">
                No stockists match that search yet — try a city name.
              </p>}
          </div>}
      </section>

      {/* Become a stockist */}
      <section id="become" className="w-full scroll-mt-28 bg-coffee py-20 text-paper md:py-28" data-id="element-960" data-mp="3d0p8350tmbo7">
        <div className="mx-auto max-w-[1400px] px-6" data-id="element-961" data-mp="5qvmeml6yp8r">
          <SectionHeading tone="light" eyebrow="Become a stockist" title="Stock Ecoffee Cup® in your store." subtitle="From a 24-unit opening order, with merchandising support and no exclusivity lock-in." data-id="element-962" data-mp="5s6s4ry7672i" />

          <div className="mt-14 grid gap-14 lg:grid-cols-[1fr_1.15fr] lg:gap-20" data-id="element-963" data-mp="jqoaea5c2rd1">
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-1" data-id="element-964" data-mp="oprensdhotjy">
              {stockistBenefits.map(b => <div key={b.title} className="border-t border-paper/20 pt-6" data-id="element-965" data-mp="tmrkc26ffuaj">
                  <p className="type-ui text-base text-paper" data-id="element-966" data-mp="yej33qjccvq4">{b.title}</p>
                  <p className="type-body mt-2 text-sm text-paper/60" data-id="element-967" data-mp="12g4ok09xyibx">
                    {b.body}
                  </p>
                </div>)}
            </div>

            <EnquiryForm tone="dark" submitLabel="Apply to stock" note="Trade pricing sent on approval." fields={[{
            name: 'store',
            label: 'Store name',
            half: true,
            required: true
          }, {
            name: 'contact',
            label: 'Contact person',
            half: true,
            required: true
          }, {
            name: 'email',
            label: 'Email',
            type: 'email',
            half: true,
            required: true
          }, {
            name: 'phone',
            label: 'Phone',
            type: 'tel',
            half: true
          }, {
            name: 'province',
            label: 'Province',
            type: 'select',
            half: true,
            required: true,
            options: ['Western Cape', 'Gauteng', 'KwaZulu-Natal', 'Eastern Cape', 'Free State', 'Other']
          }, {
            name: 'type',
            label: 'Type of business',
            type: 'select',
            half: true,
            options: ['Café / roastery', 'Design store', 'Grocer', 'Other']
          }, {
            name: 'about',
            label: 'Tell us about your store',
            type: 'textarea',
            required: true
          }]} data-id="element-968" data-mp="17dmm6uiidb4m" />
          </div>
        </div>
      </section>
    </div>;
}